// Components
import { Stack, Text, Alert, Textarea } from "@mantine/core";
import {
  IconClipboard,
  IconStethoscope,
  IconHeartHandshake,
  IconInfoCircle,
  IconAlertTriangle,
  IconCircleCheck,
} from "@tabler/icons-react";
import { SteppedForm, FormStep } from "@benchmark/ui/components/atomic";
import { CardSelect } from "@benchmark/ui/components/atomic";
import { FormField } from "@benchmark/ui/components/atomic";
import { FormSection } from "@benchmark/ui/components/atomic";
import { FormGroup } from "@benchmark/ui/components/atomic";
import { TrimmedTextInput as TextInput } from "@benchmark/ui/components/atomic";
import { CoCMReferralToggle } from "@/components/domain/care-plan/CoCMReferralToggle";
import { ReferralServiceNeedsFields } from "@/components/domain/care-plan/ReferralServiceNeedsFields";

// Hooks
import {
  startTransition,
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from "react";
import { useForm } from "@mantine/form";
import { useMedplum } from "@medplum/react";
import { useCurrentPractitionerRole } from "@/hooks/shared/useCurrentPractitionerRole";
import { usePatientResource } from "@/hooks/shared/useFHIRResource";
import { usePatientCarePlans } from "@/hooks/domain/usePatientCarePlans";

// Types
import type { ReactNode } from "react";
import type { CarePlanType } from "@benchmark/core/types";
import type { Patient } from "@medplum/fhirtypes";
import type { EnhancedSelectOption } from "@/types/ui/component-props";
import {
  createEmptyServiceNeedFormItem,
  type ServiceNeedFormItem,
} from "@benchmark/core/types";

// Utils
import { enrollInIntake } from "@benchmark/core/workflows";
import { enrollInTriage } from "@benchmark/core/workflows";
import { enrollInCoCM } from "@benchmark/core/workflows";
import { getCarePlanCategoryDisplay } from "@benchmark/core/constants";
import {
  validateTriageCarePlanCreation,
  validateCoCMCarePlanCreation,
} from "@benchmark/core/validators";
import {
  getActiveCarePlans,
  hasGeneratedCadencedFollowups,
} from "@benchmark/core/services";
import { getCoCMConsentFromPatient } from "@benchmark/core/resolvers";
import {
  showSuccessNotification,
  showErrorNotification,
} from "@benchmark/ui/utils";
import { mapServiceNeedsToServiceRequestInputs } from "@benchmark/core/adapters";

/**
 * Props for the enrollment flow component.
 * @agentic connections
 */
export type EnrollmentFlowProps = {
  patientId: string;
  /**
   * When provided, the modal opens with that care plan type preselected and
   * skips Step 1. Used by quick-action entry points (e.g. the patient FAB
   * "Add Triage Plan" item).
   */
  preselectCarePlanType?: CarePlanType;
  onSuccess?: (carePlanType: CarePlanType) => void;
  onCancel?: () => void;
};

/**
 * Form data shape for care plan enrollment.
 * @agentic connections
 */
type EnrollmentFormData = {
  carePlanType: CarePlanType | null;
  consentDate: string;
  notes: string;
  careTeamId: string;
  includeReferrals: boolean;
  serviceNeeds: ServiceNeedFormItem[];
  requiresApproval: boolean;
};

/**
 * Care plan type options for enrollment selection.
 * @agentic connections
 */
const CARE_PLAN_OPTIONS: Array<{
  type: CarePlanType;
  display: string;
  description: string;
  requiresIntake: boolean;
  icon: ReactNode;
}> = [
  {
    type: "intake",
    display: "Intake",
    description: "Initial Member Intake & Onboarding",
    requiresIntake: false,
    icon: <IconClipboard size={20} />,
  },
  {
    type: "triage",
    display: "Triage",
    description: "Benchmark Care Triage & Navigation",
    requiresIntake: true,
    icon: <IconStethoscope size={20} />,
  },
  {
    type: "cocm",
    display: "CoCM",
    description: "Collaborative Care Management",
    requiresIntake: true,
    icon: <IconHeartHandshake size={20} />,
  },
];

/**
 * Modal-based stepped form for enrolling patients in new care plans.
 * @agentic connections
 */
export const EnrollmentFlow = ({
  patientId,
  preselectCarePlanType,
  onSuccess,
  onCancel,
}: EnrollmentFlowProps) => {
  const medplum = useMedplum();
  const { practitionerRole } = useCurrentPractitionerRole();
  const { data: patientResource } = usePatientResource(patientId);
  const patient = patientResource as Patient | undefined;
  const { carePlans, refetch } = usePatientCarePlans(patientId);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasPriorCadencedFollowups, setHasPriorCadencedFollowups] = useState<
    boolean | null
  >(null);

  const form = useForm<EnrollmentFormData>({
    initialValues: {
      carePlanType: preselectCarePlanType ?? null,
      consentDate: "",
      notes: "",
      careTeamId: "",
      includeReferrals: false,
      serviceNeeds:
        preselectCarePlanType === "triage"
          ? [createEmptyServiceNeedFormItem()]
          : [],
      requiresApproval: true,
    },
    validate: {
      carePlanType: (value) =>
        value === null ? "Please select a care plan type" : null,
      consentDate: (value, values) => {
        if (values.carePlanType === "cocm" && !value) {
          return "Consent date is required for CoCM enrollment";
        }
        return null;
      },
      serviceNeeds: (value, values) => {
        const requiresServiceNeeds =
          values.carePlanType === "triage" ||
          (values.carePlanType === "cocm" && values.includeReferrals);

        if (!requiresServiceNeeds) {
          return null;
        }
        if (!value || value.length === 0) {
          return "At least one service need is required for this program";
        }
        if (value.some((need) => !need.serviceType)) {
          return "Each service need must have a service type selected";
        }
        if (value.some((need) => need.modes.length === 0)) {
          return "Each service need must have at least one mode selected";
        }
        return null;
      },
    },
  });

  // Lazily resolve cadenced-followup history for the patient. Surfaces an
  // info alert in Step 2 when relevant; the workflow itself enforces the
  // gate regardless of UI state (D8 + D10).
  useEffect(() => {
    if (!patient || form.values.carePlanType !== "triage") {
      // Defer reset out of the synchronous effect body to satisfy
      // react-hooks/set-state-in-effect. The alert that reads this
      // state is gated on carePlanType === "triage" at render time,
      // so the brief desync before the transition flushes cannot show
      // stale data.
      startTransition(() => setHasPriorCadencedFollowups(null));
      return;
    }
    let cancelled = false;
    hasGeneratedCadencedFollowups(medplum, patient.id ?? "")
      .then((result) => {
        if (!cancelled) setHasPriorCadencedFollowups(result);
      })
      .catch((error) => {
        console.warn(
          "Failed to check cadenced-followup history for patient:",
          error,
        );
        if (!cancelled) setHasPriorCadencedFollowups(null);
      });
    return () => {
      cancelled = true;
    };
  }, [patient, form.values.carePlanType, medplum]);

  // Prefill the CoCM consent date from the patient's recorded consent so the
  // date captured on the patient form flows into enrollment rather than being
  // re-typed. Seeds at most once, the first time the patient resolves, and only
  // while the field is still empty — so it never clobbers a date the user typed
  // or intentionally cleared, even if the patient resource later refetches (D9).
  const hasAttemptedConsentPrefill = useRef(false);
  useEffect(() => {
    if (!patient) return;
    if (hasAttemptedConsentPrefill.current) return;
    hasAttemptedConsentPrefill.current = true;
    if (form.values.consentDate) return;
    const consentDate = getCoCMConsentFromPatient(patient)?.date;
    if (consentDate) {
      form.setFieldValue("consentDate", consentDate);
    }
    // Intentionally keyed on `patient` only: re-running on every `form` change
    // would be wasteful, and the ref + value guards above prevent overwrites.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient]);

  const selectedTypeCarePlans = useMemo(() => {
    if (!form.values.carePlanType) return [];
    return (carePlans ?? []).filter(
      (carePlan) =>
        carePlan.category?.[0]?.coding?.[0]?.code === form.values.carePlanType,
    );
  }, [carePlans, form.values.carePlanType]);

  const hasActivePlanOfType = useMemo(
    () =>
      selectedTypeCarePlans.some(
        (carePlan) =>
          carePlan.status === "active" || carePlan.status === "draft",
      ),
    [selectedTypeCarePlans],
  );

  const activePlansOfTypeCount = useMemo(
    () =>
      selectedTypeCarePlans.filter(
        (carePlan) =>
          carePlan.status === "active" || carePlan.status === "draft",
      ).length,
    [selectedTypeCarePlans],
  );

  const hasRevokedPlanOfType = useMemo(
    () =>
      selectedTypeCarePlans.some((carePlan) => carePlan.status === "revoked"),
    [selectedTypeCarePlans],
  );

  const hasCompletedPlanOfType = useMemo(
    () =>
      selectedTypeCarePlans.some((carePlan) => carePlan.status === "completed"),
    [selectedTypeCarePlans],
  );

  // Create CardSelect options with icons
  const carePlanSelectOptions: EnhancedSelectOption[] = useMemo(() => {
    return CARE_PLAN_OPTIONS.map((option) => {
      return {
        value: option.type,
        label: option.display,
        description: option.description,
        icon: option.icon,
      };
    });
  }, []);

  // Handle form submission
  const handleSubmit = useCallback(
    async (values: EnrollmentFormData) => {
      if (!patient || !practitionerRole || !values.carePlanType) {
        showErrorNotification("Patient or practitioner role not available");
        return;
      }

      setIsSubmitting(true);
      const type = values.carePlanType;
      const referralServiceRequestDetails =
        type === "triage" || (type === "cocm" && values.includeReferrals)
          ? mapServiceNeedsToServiceRequestInputs(values.serviceNeeds)
          : undefined;

      // Validate eligibility for care plans that require validation
      if (type !== "intake") {
        try {
          if (!patient.id) {
            throw new Error("Patient ID is required");
          }
          const existingPlans = await getActiveCarePlans(medplum, patient.id);
          let validation;

          if (type === "triage") {
            validation = validateTriageCarePlanCreation(existingPlans);
          } else if (type === "cocm") {
            validation = validateCoCMCarePlanCreation(existingPlans);
          } else {
            return;
          }

          if (!validation.isValid) {
            // Set validation errors in form
            form.setFieldError("carePlanType", validation.errors.join(". "));
            setIsSubmitting(false);
            return;
          }
        } catch (error) {
          showErrorNotification(
            `Validation error: ${error instanceof Error ? error.message : "Unknown error"}`,
          );
          setIsSubmitting(false);
          return;
        }
      }

      // Perform enrollment
      try {
        if (type === "intake") {
          await enrollInIntake(medplum, {
            patient,
            practitionerRole,
            careTeamId: values.careTeamId || undefined,
            notes: values.notes || undefined,
          });
          showSuccessNotification(
            `Successfully enrolled patient in ${getCarePlanCategoryDisplay(type)}`,
          );
        } else if (type === "triage") {
          await enrollInTriage(medplum, {
            patient,
            practitionerRole,
            careTeamId: values.careTeamId || undefined,
            notes: values.notes || undefined,
            serviceRequestDetails:
              referralServiceRequestDetails &&
              referralServiceRequestDetails.length > 0
                ? referralServiceRequestDetails
                : undefined,
            requiresApproval:
              referralServiceRequestDetails &&
              referralServiceRequestDetails.length > 0
                ? values.requiresApproval
                : undefined,
          });
          showSuccessNotification(
            `Successfully enrolled patient in ${getCarePlanCategoryDisplay(type)}`,
          );
        } else if (type === "cocm") {
          const cocmResult = await enrollInCoCM(medplum, {
            patient,
            practitionerRole,
            careTeamId: values.careTeamId || undefined,
            consentDate: values.consentDate || undefined,
            // Anchor the CoCM episode start (CarePlan.period.start) to the
            // captured consent date rather than the transaction timestamp, so
            // the D1/D2 billing window and first-month detection reflect the
            // real enrollment date (D10).
            periodStart: values.consentDate || undefined,
            notes: values.notes || undefined,
            serviceRequestDetails:
              referralServiceRequestDetails &&
              referralServiceRequestDetails.length > 0
                ? referralServiceRequestDetails
                : undefined,
            requiresApproval:
              referralServiceRequestDetails &&
              referralServiceRequestDetails.length > 0
                ? values.requiresApproval
                : undefined,
          });
          // CoCM uses transaction bundles which may have search index lag
          // Add delay to allow Medplum's search index to update before refetching
          await new Promise((resolve) => setTimeout(resolve, 1000));

          const successCopy = cocmResult.triageCarePlan
            ? `Successfully enrolled patient in ${getCarePlanCategoryDisplay(type)} with sibling Triage care plan`
            : `Successfully enrolled patient in ${getCarePlanCategoryDisplay(type)}`;

          showSuccessNotification(successCopy);
        }
        await refetch();
        onSuccess?.(type);
      } catch (error) {
        showErrorNotification(
          `Failed to enroll: ${error instanceof Error ? error.message : "Unknown error"}`,
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [patient, practitionerRole, medplum, refetch, onSuccess, form],
  );

  // Get selected care plan option for display
  const selectedCarePlanOption = useMemo(() => {
    if (!form.values.carePlanType) return null;
    return CARE_PLAN_OPTIONS.find(
      (opt) => opt.type === form.values.carePlanType,
    );
  }, [form.values.carePlanType]);

  // Form steps configuration
  const steps = useMemo(
    () => [
      {
        id: "select-type",
        title: "Select Care Plan",
        subtitle: "Choose the care plan type to enroll this patient.",
        requiredFields: ["carePlanType"],
      },
      {
        id: "details",
        title: "Enrollment Details",
        subtitle: "Complete enrollment information.",
        requiredFields: [
          ...(form.values.carePlanType === "cocm" ? ["consentDate"] : []),
          ...(form.values.carePlanType === "triage" ||
          (form.values.carePlanType === "cocm" && form.values.includeReferrals)
            ? ["serviceNeeds"]
            : []),
        ],
      },
    ],
    [form.values.carePlanType, form.values.includeReferrals],
  );

  if (!patient) {
    return <Text c="dimmed">Loading patient data...</Text>;
  }

  return (
    <SteppedForm
      form={form}
      steps={steps}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      submitText="Enroll"
      showCancel={true}
      isSubmitting={isSubmitting}
      initialStepIndex={preselectCarePlanType ? 1 : 0}
    >
      {/* Step 1: Care Plan Selection */}
      <FormStep id="select-type">
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Select a care plan type to enroll this patient. Enrollment will
            validate prerequisites and create the care plan automatically.
          </Text>

          <FormField
            label="Care Plan Type"
            error={form.errors.carePlanType as string}
          >
            <CardSelect
              data={carePlanSelectOptions}
              value={form.values.carePlanType}
              onChange={(value) => {
                form.setFieldValue(
                  "carePlanType",
                  value as CarePlanType | null,
                );
                form.clearFieldError("carePlanType");
                if (value !== "cocm") {
                  form.setFieldValue("includeReferrals", false);
                }
                if (value === "triage") {
                  if (form.values.serviceNeeds.length === 0) {
                    form.setFieldValue("serviceNeeds", [
                      createEmptyServiceNeedFormItem(),
                    ]);
                  }
                  form.setFieldValue("requiresApproval", true);
                } else {
                  form.setFieldValue("serviceNeeds", []);
                  form.setFieldValue("requiresApproval", true);
                  form.clearFieldError("serviceNeeds");
                }
              }}
              error={!!form.errors.carePlanType}
              columns={1}
            />
          </FormField>

          {form.values.carePlanType &&
            hasActivePlanOfType &&
            form.values.carePlanType === "triage" && (
              <Alert
                icon={<IconInfoCircle size={16} />}
                title="Additional Triage plan"
                color="blue"
                variant="light"
              >
                <Text size="sm">
                  This member has {activePlansOfTypeCount} active Triage care
                  plan{activePlansOfTypeCount === 1 ? "" : "s"}. Continuing will
                  create a <strong>new</strong> Triage plan alongside the
                  existing one{activePlansOfTypeCount === 1 ? "" : "s"};
                  existing plans are not modified.
                </Text>
              </Alert>
            )}

          {form.values.carePlanType &&
            hasActivePlanOfType &&
            form.values.carePlanType !== "triage" && (
              <Alert
                icon={<IconAlertTriangle size={16} />}
                title="Alert!"
                color="red"
                variant="light"
              >
                <Text size="sm">
                  Records indicate this member already has an active{" "}
                  {getCarePlanCategoryDisplay(form.values.carePlanType)} care
                  plan. Continuing will revoke the existing plan and cancel the
                  associated tasks.
                </Text>
              </Alert>
            )}

          {form.values.carePlanType &&
            !hasActivePlanOfType &&
            hasRevokedPlanOfType && (
              <Alert
                icon={<IconInfoCircle size={16} />}
                title="Warning!"
                color="yellow"
                variant="light"
              >
                <Text size="sm">
                  Records indicate this member has previously been disenrolled
                  from this care plan. Continuing will re-enroll the member in
                  this plan and re-create any associated tasks.
                </Text>
              </Alert>
            )}

          {form.values.carePlanType &&
            !hasActivePlanOfType &&
            !hasRevokedPlanOfType &&
            hasCompletedPlanOfType && (
              <Alert
                icon={<IconCircleCheck size={16} />}
                title="Re-enrolling?"
                color="green"
                variant="light"
              >
                <Text size="sm">
                  Records indicate this member has previously completed this
                  care plan. Continuing will re-enroll the member and re-create
                  any associated tasks.
                </Text>
              </Alert>
            )}
        </Stack>
      </FormStep>

      {/* Step 2: Care Plan Details */}
      <FormStep id="details">
        {form.values.carePlanType && selectedCarePlanOption ? (
          <Stack gap="md">
            {/* Care Plan Information */}
            <FormSection title="Care Plan Information" showDivider={false}>
              <Alert
                icon={<IconInfoCircle size={16} />}
                title={selectedCarePlanOption.display}
                color="blue"
                variant="light"
              >
                <Stack gap="xs">
                  {form.values.carePlanType === "cocm" && (
                    <Text size="sm">
                      Collaborative Care Management provides comprehensive
                      behavioral health support through regular check-ins,
                      psychiatric consultations, and coordinated care.
                    </Text>
                  )}
                  {form.values.carePlanType === "triage" && (
                    <Text size="sm">
                      Triage helps navigate members to appropriate resources and
                      care pathways based on their needs.
                    </Text>
                  )}
                  {form.values.carePlanType === "intake" && (
                    <Text size="sm">
                      Intake is the initial onboarding process for new members,
                      including assessment and care plan selection.
                    </Text>
                  )}
                </Stack>
              </Alert>
            </FormSection>

            {/* Type-Specific Fields */}
            {form.values.carePlanType === "cocm" && (
              <FormSection
                title="CoCM Enrollment Information"
                showDivider={false}
              >
                <FormGroup>
                  <FormField
                    label="Consent Date"
                    description="Date when the patient consented to receive CoCM services"
                    required
                    error={form.errors.consentDate as string}
                  >
                    <TextInput
                      type="date"
                      value={form.values.consentDate}
                      onChange={(e) => {
                        form.setFieldValue("consentDate", e.target.value);
                        form.clearFieldError("consentDate");
                      }}
                      error={!!form.errors.consentDate}
                    />
                  </FormField>
                </FormGroup>
                <CoCMReferralToggle
                  checked={form.values.includeReferrals}
                  onChange={(includeReferrals) => {
                    form.setFieldValue("includeReferrals", includeReferrals);
                    if (includeReferrals) {
                      if (form.values.serviceNeeds.length === 0) {
                        form.setFieldValue("serviceNeeds", [
                          createEmptyServiceNeedFormItem(),
                        ]);
                      }
                      form.setFieldValue("requiresApproval", true);
                    } else {
                      form.setFieldValue("serviceNeeds", []);
                      form.setFieldValue("requiresApproval", true);
                      form.clearFieldError("serviceNeeds");
                    }
                  }}
                />
              </FormSection>
            )}

            {form.values.carePlanType === "triage" &&
              hasPriorCadencedFollowups && (
                <Alert
                  icon={<IconInfoCircle size={16} />}
                  title="Cadenced followup tasks"
                  color="blue"
                  variant="light"
                >
                  <Text size="sm">
                    This member has previously generated cadenced-followup
                    tasks. The new Triage plan will not duplicate them.
                  </Text>
                </Alert>
              )}

            {(form.values.carePlanType === "triage" ||
              (form.values.carePlanType === "cocm" &&
                form.values.includeReferrals)) && (
              <ReferralServiceNeedsFields
                includeSection
                serviceNeeds={form.values.serviceNeeds}
                requiresApproval={form.values.requiresApproval}
                serviceNeedsError={
                  form.errors.serviceNeeds
                    ? String(form.errors.serviceNeeds)
                    : null
                }
                onServiceNeedsChange={(items) => {
                  form.setFieldValue("serviceNeeds", items);
                  form.clearFieldError("serviceNeeds");
                }}
                onRequiresApprovalChange={(checked) =>
                  form.setFieldValue("requiresApproval", checked)
                }
              />
            )}

            {/* Common Fields */}
            <FormSection title="Additional Information" showDivider={false}>
              <FormField
                label="Notes"
                description="Optional notes about this enrollment"
              >
                <Textarea
                  value={form.values.notes}
                  onChange={(e) => {
                    form.setFieldValue("notes", e.target.value);
                  }}
                  rows={3}
                  placeholder="Add any additional notes about this enrollment..."
                />
              </FormField>
            </FormSection>
          </Stack>
        ) : (
          <Text c="dimmed">
            Please select a care plan type in the previous step.
          </Text>
        )}
      </FormStep>
    </SteppedForm>
  );
};
